import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Drawer,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Modal,
    Pagination,
    Paper,
    Select,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import NumbersOutlinedIcon from "@mui/icons-material/NumbersOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import styled from "styled-components";
import { useDebounce } from "react-use";

import useIndex from "../../../hooks/useIndex";
import validErrors from "../../../hooks/validErrors";
import { getGamesByUser } from "../../../data/api/game/GameRoutes";
import GameResume from "../game/GameResume";

const fieldStyles = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "14px",
        background: "#fff",
        color: "#10285f",
        fontWeight: 700,
        transition: "0.25s ease",
        "& fieldset": {
            borderColor: "rgba(15, 34, 77, 0.16)",
        },
        "&:hover fieldset": {
            borderColor: "rgba(214, 170, 58, 0.7)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#d6aa3a",
            borderWidth: "2px",
        },
    },
    "& .MuiInputLabel-root": {
        color: "rgba(15, 34, 77, 0.65)",
        fontWeight: 700,
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#10285f",
    },
    "& .MuiSvgIcon-root": {
        color: "#10285f",
    },
};

const panelButtonStyles = {
    borderRadius: "14px",
    minHeight: 44,
    fontWeight: 900,
    textTransform: "uppercase",
};

const History = ({ user_search }) => {
    const { auth, token } = useIndex();
    const isAdmin = auth?.role_id == process.env.REACT_APP_ADMIN_ROLE;

    const isMobile = useMediaQuery("(max-width:900px)");
    const isUserHistory = Boolean(user_search);

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);

    const [open, setOpen] = useState(false);
    const [gameResult, setGameResult] = useState(null);

    const [codeSearch, setCodeSearch] = useState("");
    const [hitsSearch, setHitsSearch] = useState("t");

    const [userName, setUserName] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [sortBy, setSortBy] = useState("created_at");
    const [sortDir, setSortDir] = useState("desc");

    const [filtersOpen, setFiltersOpen] = useState(false);

    const userUuid = useMemo(() => {
        if (user_search) return user_search;
        if (isAdmin) return "0";
        return auth?.uuid;
    }, [user_search, isAdmin, auth?.uuid]);

    const total = payload?.meta?.total ?? 0;
    const lastPage = payload?.meta?.last_page ?? 1;

    const hitsColor = (h) => {
        const n = Number(h ?? 0);
        if (n >= 4) return "success";
        if (n >= 2) return "warning";
        if (n >= 1) return "info";
        return "default";
    };

    const copyCode = async (full) => {
        try {
            await navigator.clipboard.writeText(full);
        } catch (e) { }
    };

    const openDetail = (row) => {
        setGameResult(row);
        setOpen(true);
    };

    const closeDetail = () => {
        setOpen(false);
        setGameResult(null);
    };

    const resetFilters = () => {
        setCodeSearch("");
        setHitsSearch("t");
        setUserName("");
        setDateFrom("");
        setDateTo("");
        setSortBy("created_at");
        setSortDir("desc");
        setPage(1);
    };

    const [isReady, cancel] = useDebounce(
        () => {
            setPage(1);
            loadData(1, rowsPerPage);
        },
        500,
        [codeSearch, hitsSearch, userName, dateFrom, dateTo, sortBy, sortDir],
    );

    useEffect(() => {
        cancel();
    }, []);

    useEffect(() => {
        loadData(page, rowsPerPage);
    }, [page, rowsPerPage]);

    const loadData = async (p, rpp) => {
        setLoading(true);
        try {
            const response = await getGamesByUser(
                token,
                hitsSearch,
                userUuid,
                rpp,
                p,
                (codeSearch ?? "").toUpperCase(),
                {
                    user_name: userName,
                    date_from: dateFrom,
                    date_to: dateTo,
                    sort_by: sortBy,
                    sort_dir: sortDir,
                },
            );

            if (response?.data?.success) {
                setPayload(response.data);
            } else {
                setPayload({ data: [], meta: { total: 0, last_page: 1 } });
            }
        } catch (error) {
            validErrors(error);
            setPayload({ data: [], meta: { total: 0, last_page: 1 } });
        } finally {
            setLoading(false);
        }
    };

    const FiltersPanel = (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 900,
                        color: "#10285f",
                        letterSpacing: 0.3,
                    }}
                >
                    Panel
                </Typography>

                {isMobile && (
                    <IconButton
                        size="small"
                        onClick={() => setFiltersOpen(false)}
                        sx={{
                            color: "#10285f",
                            background: "rgba(16, 40, 95, 0.06)",
                            "&:hover": {
                                background: "rgba(214, 170, 58, 0.16)",
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </Stack>

            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    borderRadius: 3,
                    border: "1px solid rgba(15, 34, 77, 0.10)",
                    background: "rgba(16, 40, 95, 0.045)",
                    mb: 1.8,
                }}
            >
                <Stack spacing={1}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip
                            label={`Total: ${total}`}
                            size="small"
                            sx={{
                                fontWeight: 900,
                                color: "#10285f",
                                background: "rgba(214, 170, 58, 0.20)",
                                border: "1px solid rgba(214, 170, 58, 0.35)",
                            }}
                        />

                        <Chip
                            label={`Página: ${page}/${lastPage}`}
                            size="small"
                            variant="outlined"
                            sx={{
                                fontWeight: 800,
                                color: "#10285f",
                                borderColor: "rgba(15, 34, 77, 0.18)",
                                background: "#fff",
                            }}
                        />
                    </Stack>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {hitsSearch !== "t" && (
                            <Chip
                                size="small"
                                label={`Aciertos: ${hitsSearch}`}
                                color={hitsColor(hitsSearch)}
                                sx={{ fontWeight: 800 }}
                            />
                        )}

                        {codeSearch && (
                            <Chip
                                size="small"
                                label={`Código: ${codeSearch}`}
                                variant="outlined"
                                sx={{
                                    fontWeight: 800,
                                    color: "#10285f",
                                    borderColor: "rgba(15, 34, 77, 0.18)",
                                    background: "#fff",
                                }}
                            />
                        )}
                    </Stack>
                </Stack>
            </Paper>

            <Divider
                sx={{
                    mb: 1.8,
                    borderColor: "rgba(15, 34, 77, 0.10)",
                }}
            />

            <Grid container spacing={1.35}>
                <Grid item xs={12} sm={6} md={isUserHistory ? 3 : 12}>
                    <TextField
                        label="Código"
                        size="small"
                        fullWidth
                        value={codeSearch}
                        onChange={(e) => setCodeSearch(e.target.value.toUpperCase())}
                        sx={fieldStyles}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={isUserHistory ? 3 : 12}>
                    <FormControl fullWidth size="small" sx={fieldStyles}>
                        <InputLabel id="hits">Aciertos</InputLabel>
                        <Select
                            labelId="hits"
                            value={hitsSearch}
                            label="Aciertos"
                            onChange={(e) => setHitsSearch(e.target.value)}
                        >
                            <MenuItem value={"t"}>Todas</MenuItem>
                            <MenuItem value={"0"}>0</MenuItem>
                            <MenuItem value={"1"}>1</MenuItem>
                            <MenuItem value={"2"}>2</MenuItem>
                            <MenuItem value={"3"}>3</MenuItem>
                            <MenuItem value={"4"}>4</MenuItem>
                            <MenuItem value={"5"}>5</MenuItem>
                            <MenuItem value={"6"}>6</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {isAdmin && !user_search && (
                    <Grid item xs={12} sm={6} md={12}>
                        <TextField
                            label="Usuario"
                            size="small"
                            fullWidth
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            sx={fieldStyles}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonOutlineOutlinedIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                )}

                <Grid item xs={12} sm={6} md={isUserHistory ? 3 : 12}>
                    <TextField
                        label="Desde"
                        type="date"
                        size="small"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={fieldStyles}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={isUserHistory ? 3 : 12}>
                    <TextField
                        label="Hasta"
                        type="date"
                        size="small"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={fieldStyles}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={isUserHistory ? 3 : 12}>
                    <FormControl fullWidth size="small" sx={fieldStyles}>
                        <InputLabel id="sortBy">Orden</InputLabel>
                        <Select
                            labelId="sortBy"
                            value={sortBy}
                            label="Orden"
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <MenuItem value={"created_at"}>Fecha</MenuItem>
                            <MenuItem value={"hits"}>Aciertos</MenuItem>
                            <MenuItem value={"id"}>ID</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={isUserHistory ? 3 : 12}>
                    <FormControl fullWidth size="small" sx={fieldStyles}>
                        <InputLabel id="sortDir">Dir</InputLabel>
                        <Select
                            labelId="sortDir"
                            value={sortDir}
                            label="Dir"
                            onChange={(e) => setSortDir(e.target.value)}
                        >
                            <MenuItem value={"desc"}>Desc</MenuItem>
                            <MenuItem value={"asc"}>Asc</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={isUserHistory ? 3 : 12}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => {
                            setPage(1);
                            loadData(1, rowsPerPage);
                            if (isMobile) setFiltersOpen(false);
                        }}
                        sx={{
                            ...panelButtonStyles,
                            background: "linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)",
                            boxShadow: "0 6px 14px rgba(15, 34, 77, 0.18)",
                            "&:hover": {
                                background: "linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)",
                                boxShadow: "0 8px 18px rgba(15, 34, 77, 0.24)",
                            },
                        }}
                    >
                        Aplicar
                    </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={isUserHistory ? 3 : 12}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={resetFilters}
                        startIcon={<RestartAltIcon />}
                        sx={{
                            ...panelButtonStyles,
                            color: "#10285f",
                            borderColor: "rgba(214, 170, 58, 0.75)",
                            background: "#fff",
                            "&:hover": {
                                borderColor: "#d6aa3a",
                                background: "rgba(214, 170, 58, 0.12)",
                            },
                        }}
                    >
                        Limpiar
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );

    const CompactCard = ({ item }) => {
        const moves = [
            item?.move1,
            item?.move2,
            item?.move3,
            item?.move4,
            item?.move5,
            item?.move6,
        ];

        const fullCode = item?.complete_code ?? item?.code ?? "";

        return (
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 1.5, md: 2 },
                    borderRadius: 3,
                    border: "1px solid rgba(15, 34, 77, 0.12)",
                    background: "linear-gradient(180deg, #ffffff 0%, #f8faff 100%)",
                    transition: "0.25s ease",
                    "&:hover": {
                        borderColor: "rgba(214, 170, 58, 0.55)",
                        boxShadow: "0 10px 24px rgba(15, 34, 77, 0.10)",
                        transform: "translateY(-1px)",
                    },
                }}
            >
                <Stack spacing={1.5}>
                    {/* HEADER */}
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1.2}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Chip
                                label={`# ${item?.id}`}
                                size="small"
                                sx={{
                                    fontWeight: 900,
                                    color: "#10285f",
                                    background: "rgba(16, 40, 95, 0.08)",
                                    border: "1px solid rgba(16, 40, 95, 0.14)",
                                }}
                            />

                            <Chip
                                label={`${item?.hits ?? 0} aciertos`}
                                size="small"
                                sx={{
                                    fontWeight: 900,
                                    color: Number(item?.hits ?? 0) > 0 ? "#8a5a00" : "#555",
                                    background:
                                        Number(item?.hits ?? 0) > 0
                                            ? "rgba(214, 170, 58, 0.18)"
                                            : "rgba(0,0,0,0.06)",
                                }}
                            />

                            {/* Estado del premio: solo aparece para
                                jugadas ganadoras. Verde = reclamado
                                (jugadas ya cargadas o premio liberado);
                                amarillo/outline = pendiente. */}
                            {item?.prize_status === "claimed" && (
                                <Chip
                                    label="Premio reclamado"
                                    size="small"
                                    icon={<CheckCircleOutlineIcon fontSize="small" />}
                                    sx={{
                                        fontWeight: 900,
                                        color: "#1e6a36",
                                        background: "rgba(46, 125, 50, 0.14)",
                                        border: "1px solid rgba(46, 125, 50, 0.32)",
                                        "& .MuiChip-icon": { color: "#1e6a36" },
                                    }}
                                />
                            )}

                            {item?.prize_status === "pending" && (
                                <Chip
                                    label="Premio pendiente"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        fontWeight: 900,
                                        color: "#8a5a00",
                                        borderColor: "rgba(214, 170, 58, 0.55)",
                                        background: "rgba(214, 170, 58, 0.08)",
                                    }}
                                />
                            )}

                            {isAdmin && !user_search && (
                                <Chip
                                    size="small"
                                    label={item?.user ?? "-"}
                                    sx={{
                                        fontWeight: 700,
                                        color: "#1a2f5c",
                                        background: "#fff",
                                        border: "1px solid rgba(15, 34, 77, 0.16)",
                                    }}
                                />
                            )}
                        </Stack>

                        <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Copiar código completo">
                                <IconButton
                                    size="small"
                                    onClick={() => copyCode(fullCode)}
                                    sx={{
                                        color: "#1a2f5c",
                                        background: "rgba(16, 40, 95, 0.06)",
                                        "&:hover": {
                                            background: "rgba(214, 170, 58, 0.18)",
                                            color: "#8a6500",
                                        },
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Ver detalle">
                                <IconButton
                                    size="small"
                                    onClick={() => openDetail(item)}
                                    sx={{
                                        color: "#1a2f5c",
                                        background: "rgba(16, 40, 95, 0.06)",
                                        "&:hover": {
                                            background: "rgba(214, 170, 58, 0.18)",
                                            color: "#8a6500",
                                        },
                                    }}
                                >
                                    <VisibilityOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>

                    {/* NÚMEROS */}
                    <Box
                        sx={{
                            p: 1.2,
                            borderRadius: 2,
                            background: "rgba(16, 40, 95, 0.045)",
                            border: "1px solid rgba(16, 40, 95, 0.08)",
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                display: "block",
                                mb: 0.8,
                                fontWeight: 800,
                                color: "#1a2f5c",
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                            }}
                        >
                            Números jugados
                        </Typography>

                        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                            {moves.map((m, idx) => (
                                <Box
                                    key={`${item?.id}-m-${idx}`}
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 900,
                                        color: "#10285f",
                                        background: "#fff",
                                        border: "2px solid rgba(214, 170, 58, 0.65)",
                                        boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
                                    }}
                                >
                                    {m}
                                </Box>
                            ))}
                        </Stack>
                    </Box>

                    {/* FOOTER */}
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1}
                    >
                        <Tooltip title={fullCode}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily: "monospace",
                                    color: "rgba(0,0,0,0.65)",
                                    background: "rgba(0,0,0,0.04)",
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1.5,
                                    maxWidth: { xs: "100%", sm: 320 },
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {fullCode}
                            </Typography>
                        </Tooltip>

                        <Stack direction="row" spacing={0.7} alignItems="center">
                            <CalendarMonthOutlinedIcon sx={{ fontSize: 17, color: "#d6aa3a" }} />
                            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.62)", fontWeight: 600 }}>
                                {item?.created_at ?? "-"}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Paper>
        );
    };

    return (
        <>
            <Container sx={{ py: 2 }} maxWidth={false}>
                {!user_search && (
                    <Typography
                        variant="h5"
                        textAlign="center"
                        sx={{ mb: 2, fontWeight: 900 }}
                    >
                        Historial de jugadas
                    </Typography>
                )}

                <Grid container spacing={2}>
                    {!isMobile && !isUserHistory && (
                        <Grid item xs={12} md={4} lg={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: 4,
                                    border: "1px solid rgba(214, 170, 58, 0.28)",
                                    background: "linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%)",
                                    boxShadow: "0 12px 28px rgba(15, 34, 77, 0.08)",
                                    position: "sticky",
                                    top: 16,
                                }}
                            >
                                {FiltersPanel}
                            </Paper>
                        </Grid>
                    )}

                    <Grid item xs={12} md={!isUserHistory ? 8 : 12} lg={!isUserHistory ? 9 : 12}>
                        <Stack spacing={2}>
                            {!isMobile && isUserHistory && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: 4,
                                        border: "1px solid rgba(214, 170, 58, 0.28)",
                                        background: "linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%)",
                                        boxShadow: "0 12px 28px rgba(15, 34, 77, 0.08)",
                                    }}
                                >
                                    {FiltersPanel}
                                </Paper>
                            )}

                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 1.5, md: 2.5 },
                                    borderRadius: 4,
                                    border: "1px solid rgba(15, 34, 77, 0.10)",
                                    background: "linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%)",
                                    minHeight: "60vh",
                                    boxShadow: "0 12px 28px rgba(15, 34, 77, 0.08)",
                                }}
                            >
                                <Stack spacing={1.25}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 900,
                                                color: "#10285f",
                                                fontSize: "1.15rem",
                                            }}
                                        >
                                            Resultados
                                        </Typography>

                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {isMobile && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<FilterAltOutlinedIcon />}
                                                    onClick={() => setFiltersOpen(true)}
                                                >
                                                    Filtros
                                                </Button>
                                            )}

                                            <FormControl size="small" sx={{ minWidth: 92 }}>
                                                <Select
                                                    value={rowsPerPage}
                                                    onChange={(e) => {
                                                        setRowsPerPage(Number(e.target.value));
                                                        setPage(1);
                                                    }}
                                                >
                                                    <MenuItem value={10}>10</MenuItem>
                                                    <MenuItem value={25}>25</MenuItem>
                                                    <MenuItem value={50}>50</MenuItem>
                                                    <MenuItem value={100}>100</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </Stack>

                                    <Divider />

                                    {loading ? (
                                        <Stack spacing={1.25}>
                                            {[...Array(8)].map((_, i) => (
                                                <Paper
                                                    key={i}
                                                    elevation={0}
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        border: "1px solid rgba(0,0,0,.08)",
                                                    }}
                                                >
                                                    <Skeleton variant="text" height={20} width="45%" />
                                                    <Skeleton variant="text" height={18} width="85%" />
                                                </Paper>
                                            ))}
                                        </Stack>
                                    ) : Array.isArray(payload?.data) && payload.data.length > 0 ? (
                                        <Stack spacing={1.25}>
                                            {payload.data.map((item) => (
                                                <CompactCard key={item?.id} item={item} />
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Box sx={{ py: 8, textAlign: "center" }}>
                                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                                                No hay registros
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                                Cambia los filtros o busca por código.
                                            </Typography>
                                        </Box>
                                    )}

                                    <Divider sx={{ mt: 1 }} />
                                    <PaginationContainer>
                                        <Pagination
                                            count={lastPage}
                                            page={page}
                                            onChange={(_, p) => setPage(p)}
                                            showFirstButton
                                            showLastButton
                                            size="small"
                                        />
                                    </PaginationContainer>
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>

            {isMobile && (
                <Drawer
                    anchor="right"
                    open={filtersOpen}
                    onClose={() => setFiltersOpen(false)}
                >
                    <Box
                        sx={{
                            width: 360,
                            maxWidth: "100vw",
                            p: 2,
                            background: "linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%)",
                            minHeight: "100vh",
                        }}
                    >
                        {FiltersPanel}
                    </Box>
                </Drawer>
            )}

            <Modal
                open={open}
                onClose={closeDetail}
                sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <GameResume gameResult={gameResult} handleClose={closeDetail} />
            </Modal>
        </>
    );
};

export default History;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 10px;
`;